import { RATING_STARS, TREEMAP_MAX_ITEMS } from '@/domain/reporting/reporting-shared.constants';
import { getRatingBucketColor } from '@/domain/reporting/reporting-shared.colors';
import { clamp } from '@/domain/reporting/reporting-shared.math';
import {
    type RatingBucketRow,
    type RatingDistributionLayout,
    type RatingDistributionLayoutOptions,
    type ReportTopItemRow,
    type ReportTreemapSourceRow,
    type TreemapLayoutInputItem,
} from '@/domain/reporting/reporting-shared.types';

export const normalizeRatingBuckets = (
    input?: RatingBucketRow[] | null,
): Array<{
    stars: number;
    value: number;
}> => {
    const buckets = new Map<number, number>(RATING_STARS.map((stars) => [stars, 0]));

    for (const row of input || []) {
        const stars = clamp(Math.round(row.stars), 1, 5);
        buckets.set(stars, Math.max(0, Math.round(row.value || 0)));
    }

    return RATING_STARS.map((stars) => ({
        stars,
        value: buckets.get(stars) || 0,
    }));
};

export const buildRatingDistributionLayout = (
    buckets: Array<{
        stars: number;
        value: number;
    }>,
    options: RatingDistributionLayoutOptions,
): RatingDistributionLayout | null => {
    if (!buckets.length) {
        return null;
    }

    const sortedBuckets = [...buckets].sort((left, right) => right.stars - left.stars);
    const chartAreaWidth = options.width - options.leftLabelWidth - options.rightLabelWidth - options.gap;
    const maxValue = Math.max(1, ...sortedBuckets.map((bucket) => bucket.value));
    const height =
        options.topPadding +
        options.bottomPadding +
        sortedBuckets.length * options.rowHeight +
        (sortedBuckets.length - 1) * options.rowGap;

    return {
        width: options.width,
        height,
        leftLabelWidth: options.leftLabelWidth,
        chartAreaWidth,
        rowHeight: options.rowHeight,
        rows: sortedBuckets.map((bucket, index) => {
            const y = options.topPadding + index * (options.rowHeight + options.rowGap);
            const minBarWidth =
                typeof options.minBarWidth === 'number' && Number.isFinite(options.minBarWidth)
                    ? options.minBarWidth
                    : 4;
            const barWidth = Math.max(minBarWidth, Math.round((bucket.value / maxValue) * chartAreaWidth));

            return {
                stars: bucket.stars,
                value: bucket.value,
                y,
                barWidth,
                color: getRatingBucketColor(bucket.stars),
            };
        }),
    };
};

export const sortByReviewCountDesc = <T extends { reviewCount: number }>(left: T, right: T): number =>
    right.reviewCount - left.reviewCount;

export const sortByReviewCountThenNameRuDesc = <
    T extends {
        reviewCount: number;
        name: string;
    },
>(
    left: T,
    right: T,
): number => {
    const reviewDelta = sortByReviewCountDesc(left, right);
    if (reviewDelta !== 0) {
        return reviewDelta;
    }

    return left.name.localeCompare(right.name, 'ru', { sensitivity: 'base', numeric: true });
};

export const buildRatingBucketsFromReviewRows = <
    T extends {
        reviewCount: number;
        averageRating: number | null;
    },
>(
    rows: T[],
): RatingBucketRow[] => {
    const buckets = new Map<number, number>(RATING_STARS.map((rating) => [rating, 0]));

    for (const row of rows) {
        if (row.reviewCount <= 0 || typeof row.averageRating !== 'number') {
            continue;
        }

        const bucketKey = clamp(Math.round(row.averageRating), 1, 5);
        buckets.set(bucketKey, (buckets.get(bucketKey) || 0) + row.reviewCount);
    }

    return RATING_STARS.map((stars) => ({
        stars,
        value: buckets.get(stars) || 0,
    }));
};

export const buildTopItemsFromRows = <T extends ReportTopItemRow>(
    rows: T[],
    options: {
        limit?: number;
        sortComparator?: (left: T, right: T) => number;
    } = {},
): ReportTopItemRow[] => {
    const limit =
        typeof options.limit === 'number' && Number.isFinite(options.limit)
            ? Math.max(0, Math.floor(options.limit))
            : 3;
    const sortComparator = options.sortComparator || ((left: T, right: T) => sortByReviewCountDesc(left, right));

    return [...rows]
        .sort(sortComparator)
        .slice(0, limit)
        .map((row) => ({
            name: row.name,
            reviewCount: row.reviewCount,
            averageRating: row.averageRating,
        }));
};

export const buildTreemapItemsFromRows = <T extends ReportTreemapSourceRow>(
    rows: T[],
    options: {
        maxItems?: number;
        sortComparator?: (left: T, right: T) => number;
    } = {},
): TreemapLayoutInputItem[] => {
    const maxItems =
        typeof options.maxItems === 'number' && Number.isFinite(options.maxItems)
            ? Math.max(0, Math.floor(options.maxItems))
            : TREEMAP_MAX_ITEMS;
    const sortComparator = options.sortComparator || ((left: T, right: T) => sortByReviewCountDesc(left, right));

    return [...rows]
        .filter((row) => row.reviewCount > 0)
        .sort(sortComparator)
        .slice(0, maxItems)
        .map((row) => ({
            id: row.id,
            label: row.name,
            value: row.reviewCount,
            rating: row.averageRating,
        }));
};
